import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useProjectsContext } from 'src/modules/projects/context';

import { ApiError } from '../../../common/http/mapToErrors';
import { slugify } from '../domain/slug';
import { TierFormValues } from '../domain/types';
import { tiersService } from '../infra/tiersService';
import { tiersQueryKeys } from './useTiersQuery';

interface UseCreateTierMutationOptions {
  onSuccess?: () => void;
}

export function useCreateTierMutation(options?: UseCreateTierMutationOptions) {
  const { project } = useProjectsContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: TierFormValues) => {
      const trimmedDescription = values.description.trim();
      const body = {
        name: values.name,
        slug: slugify(values.name),
        rank: values.rank,
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
        ...(values.audienceId ? { audience_id: values.audienceId } : {}),
      };
      return tiersService.create(project?.id as string, body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tiersQueryKeys.all(project?.id ?? '') });
      toast.success('Tier created successfully');
      options?.onSuccess?.();
    },
    onError: (error: ApiError) => {
      if (error.statusCode === 409) {
        toast.error('A tier with this name, rank, or audience already exists.');
      } else if (error.statusCode === 403) {
        toast.error('Admin role required.');
      } else {
        toast.error(error.message || 'Failed to create tier');
      }
    },
  });
}
